import { useLazyQuery } from '@apollo/client';
import {
  BaseUser,
  GetBaseUserResponse,
  LoginUserResponse,
  LogoutUserResponse,
} from '@/api/types/user.types';
import { GET_BASE_USER } from '@/api/queries/user.queries';
import { AuthStatus, useAuthStore } from '@/store/useAuthStore';
import { useSafeMutation } from '@/api/hooks/useSafeMutation';
import { LOGIN_USER, LOGOUT_USER } from '@/api/mutations/users.mutations';
import { ROUTES } from '@/constants/routes';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';

export function useAuth() {
  const customPush = useCustomPush();
  const [
    setIsAuthLoaded,
    setUserId,
    setAuthStatus,
    setIsAuthenticated,
    setUserInfo,
  ] = useAuthStore(state => [
    state.setIsAuthLoaded,
    state.setUserId,
    state.setAuthStatus,
    state.setIsAuthenticated,
    state.setUserInfo,
  ]);

  const [getBaseUser] = useLazyQuery<GetBaseUserResponse>(GET_BASE_USER, {
    fetchPolicy: 'network-only',
    onCompleted: data => handleUser(data?.currentUser),
    onError: handleError,
  });

  const [loginUser] = useSafeMutation<LoginUserResponse>(LOGIN_USER, {
    onCompleted: data =>
      handleUser({ id: data?.loginUser?.entity } as BaseUser),
    onError: handleError,
  });

  const [logoutUser] = useSafeMutation<LogoutUserResponse>(LOGOUT_USER, {
    onCompleted: data => {
      if (data.logoutUser.entity) {
        setUserId(null);
        handleAnonymous();
        customPush(ROUTES.home);
      }
    },
  });

  function handleAnonymous() {
    setIsAuthenticated(false);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleAuthenticated(user: BaseUser) {
    setUserId(user.id);
    setIsAuthenticated(true);
    setUserInfo(user);
    setAuthStatus(AuthStatus.Authenticated);
  }

  function handleError() {
    setIsAuthLoaded(true);
    setUserId(null);
    setUserInfo(null);
    setIsAuthenticated(false);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleUser(user: BaseUser) {
    setIsAuthLoaded(true);
    setUserId(null);

    if (user && user.id) {
      handleAuthenticated(user);
    } else {
      handleAnonymous();
    }
  }

  return { getBaseUser, loginUser, logoutUser };
}
